import * as React from 'react';
import './Admin.less';
import { Form, Input, Button, Select, Divider, message, Spin, Checkbox, Tooltip, Col, Modal, InputNumber, Row, Slider } from 'antd';
import { FormComponentProps } from 'antd/lib/form/Form';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import Routes from 'src/core/Routes';
import { withLocalize, LocalizeContextProps, Translate } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';
import TranslationDTO from 'src/models/generic/TranslationDTO';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import ActivityApiController from 'src/api/ActivityApiController';
import CultureApiController from 'src/api/CultureApiController';
import TextArea from 'antd/lib/input/TextArea';
import UpdateActivitySummaryDTO from '../../models/activity/UpdateActivitySummaryDTO';
import UpdateActivityDTO from 'src/models/activity/UpdateActivityDTO';
import AddActivitySummaryDTO from '../../models/activity/AddActivitySummaryDTO';
import Languages, { Language } from 'src/constants/Languages';

interface RouteParams {
    activityId: string;
    activitySummaryId: string;
}

interface AddEditActivitySummaryProps extends PageProps<RouteParams>, LocalizeContextProps { }

interface AddEditActivitySummaryState {
    loading: boolean;
    visible: boolean;
    isSubmitting: boolean;
    currentActivitySummary: UpdateActivitySummaryDTO;
    allCultures: Language[];
    selectedCultureId: number;
    activityMaxScore: number;
    allSummaries: UpdateActivitySummaryDTO[];

    isUpdate: boolean;
}

interface ActivitySummaryFormProps {
    currentActivitySummary: UpdateActivitySummaryDTO;
    allCultures: Language[];
    selectedCultureId: number;
    translate: (id: any) => any;
    onChange: (changedFields: any[]) => void;
    activityMaxScore: number;
    allSummaries: UpdateActivitySummaryDTO[];
}

// Form
let ActivitySummaryForm = Form.create({
    onFieldsChange(props: ActivitySummaryFormProps, changedFields: any[]) {
        props.onChange(changedFields);
    }
})(class extends React.Component<ActivitySummaryFormProps & FormComponentProps> {
    constructor(props: ActivitySummaryFormProps & FormComponentProps) {
        super(props);
    }

    private generateOptionsForSelect(keyValues: KeyValueDTO<string, number>[]): JSX.Element[] {
        return keyValues.map(keyValue => <Select.Option value={keyValue.value} key={keyValue.key} title={keyValue.key}>{keyValue.key}</Select.Option>);
    }

    private generateLanguageOptions() {
        // Only available languages
        return Languages.All.filter(x => x.isAvailable)
            .map(x => <Select.Option key={`cultureIds${x.id}`} value={x.id}>{x.name} ({x.code})</Select.Option>);
    }

    private renderTranslations(): JSX.Element[] {
        if (this.props.currentActivitySummary) {
            let translations = this.props.currentActivitySummary.translations;

            return translations.map((translation: TranslationDTO, index: number) => {
                let id = "translations[" + index + "].text";
                let culture = this.props.allCultures.find(culture => culture.id === translation.cultureId).code.toLowerCase();
                let translationId = "admin.addEditSummary." + culture;

                return this.renderStandardFormItem(
                    translationId,
                    id,
                    translation.text,
                    <TextArea rows={4} autoComplete="off" />,
                    this.props.selectedCultureId === translation.cultureId
                );
            });
        }
    }

    private renderStandardFormItem(translationId: string, id: string, initialValue, inputType, visible: boolean = true, required: boolean = true) {
        let translatedLabel = this.props.translate(translationId);

        if (visible) {
            return (
                <Form.Item
                    label={translatedLabel}>
                    {
                        this.props.form.getFieldDecorator(id, {
                            initialValue: initialValue,
                            rules: [{ required: required, message: this.props.translate('admin.common.requiredRule') }],
                        })(inputType)
                    }
                </Form.Item>
            );
        }
    }

    private renderScoreMinFormItem() {
        let translatedLabel = this.props.translate("admin.addEditSummary.formItem2");
        return (
            <Form.Item
                label={translatedLabel}>
                {
                    this.props.form.getFieldDecorator("scoreMin", {
                        initialValue: this.props.currentActivitySummary.scoreMin,
                        rules: [
                            { required: true, message: this.props.translate('admin.common.requiredRule') },
                            { validator: this.validateScoreMin }
                        ],
                    })(<InputNumber />)
                }
            </Form.Item>
        );
    }

    private validateScoreMin = (rule, value, callback) => {
        var currentMin = value;
        var currentMax = this.props.form.getFieldValue("scoreMax");
        var errorString;

        // Not within [0 - max possible score]
        if (currentMin < 0 || currentMin > this.props.activityMaxScore) {
            errorString = (this.props.translate("admin.addEditSummary.outOfRangeError") as string)
                .replace("%min%", 0 + "")
                .replace("%max%", this.props.activityMaxScore + "");
            callback(errorString);
        }

        if (currentMin > currentMax) {
            callback(this.props.translate("admin.addEditSummary.minBiggerError"));
        }

        callback();
    }

    private renderScoreMaxFormItem() {
        let translatedLabel = this.props.translate("admin.addEditSummary.formItem3");
        return (
            <Form.Item
                label={translatedLabel} >
                {
                    this.props.form.getFieldDecorator("scoreMax", {
                        initialValue: this.props.currentActivitySummary.scoreMax,
                        rules: [
                            { required: true, message: this.props.translate('admin.common.requiredRule') },
                            { validator: this.validateScoreMax }
                        ],
                    })(<InputNumber />)
                }
            </Form.Item >
        );
    }

    private validateScoreMax = (rule, value, callback) => {
        var currentMax = value;
        var currentMin = this.props.form.getFieldValue("scoreMin");
        var errorString;

        // Not within [0 - max possible score]
        if (currentMax > this.props.activityMaxScore || currentMax < 0) {
            errorString = (this.props.translate("admin.addEditSummary.outOfRangeError") as string)
                .replace("%min%", 0 + "")
                .replace("%max%", this.props.activityMaxScore + "");
            callback(errorString);
        }

        if (currentMax < currentMin) {
            callback(this.props.translate("admin.addEditSummary.maxSmallerError"));
        }

        callback();
    }

    render() {
        return (
            <Row>
                <Col span={12}>
                    <Form className="admin-form">
                        {this.renderStandardFormItem(
                            "admin.addEditSummary.formItem1",
                            "selectedCultureId",
                            this.props.selectedCultureId,
                            <Select>{this.generateLanguageOptions()}</Select>
                        )}
                        {this.renderTranslations()}
                        {this.renderScoreMinFormItem()}
                        {this.renderScoreMaxFormItem()}
                    </Form>
                </Col>
            </Row >
        );
    }
});

class AddEditActivitySummary extends React.Component<AddEditActivitySummaryProps, AddEditActivitySummaryState> {
    private formRef: React.Component<FormComponentProps>;
    private error: string = "";

    constructor(props: AddEditActivitySummaryProps) {
        super(props);
        bindAllOfThis(this, AddEditActivitySummary.prototype);

        let activityId = parseInt(this.props.match.params.activityId, 10);
        let activitySummaryId = parseInt(this.props.match.params.activitySummaryId, 10);

        let currentActivitySummary: UpdateActivitySummaryDTO = {
            activitySummaryId: activitySummaryId,
            activityId: activityId,
            scoreMin: 0,
            scoreMax: 0,
            translations: [],
        };

        this.state = {
            loading: true,
            visible: false,
            isSubmitting: false,
            allCultures: [],
            selectedCultureId: 0,
            currentActivitySummary: currentActivitySummary,
            activityMaxScore: 0,
            allSummaries: [],
            isUpdate: activitySummaryId > 0,
        };
    }

    componentDidMount(): void {
        let summariesPromise = ActivityApiController.getActivitySummaries(this.state.currentActivitySummary.activityId);
        let cultures = Languages.All.filter(l => l.isAvailable);

        summariesPromise.then(result => {
            let summaries = result.data;

            this.setState({
                allCultures: cultures,
                selectedCultureId: cultures[0].id,
                allSummaries: summaries.items
            }, this.fillForm);
        });
    }

    private fillForm(): void {
        if (this.state.isUpdate) {
            ActivityApiController.getActivitySummary(this.state.currentActivitySummary.activitySummaryId).then((result) => {
                this.setState({
                    loading: false,
                    currentActivitySummary: { ...this.state.currentActivitySummary, ...result.data },
                    activityMaxScore: result.data.maxScore,
                });

            }).catch(e => {
                message.error(this.props.translate('admin.addEditSummary.messageRetrieveError'));
            });
        } else {
            ActivityApiController.getActivity(this.state.currentActivitySummary.activityId).then((result) => {
                let translations = this.state.allCultures.map(
                    (culture: Language): TranslationDTO => {
                        let translation: TranslationDTO = {
                            cultureId: culture.id,
                            text: null
                        };

                        return translation;
                    }
                );

                // Initialize new activity form's Maximum Score value as the highest possible score you can get for that activity
                let activitySummary = {
                    translations: translations,
                    scoreMax: result.data.maxScore,
                    maxScore: result.data.maxScore
                } as UpdateActivitySummaryDTO;

                this.setState({
                    currentActivitySummary: { ...this.state.currentActivitySummary, ...activitySummary },
                    activityMaxScore: result.data.maxScore,
                    loading: false,
                });
            });
        }
    }

    private translationsAreCompleted(): boolean {
        let translations = this.state.currentActivitySummary.translations;
        let emptyTranslations = translations.filter(translation =>
            translation.text === null || translation.text === ""
        );

        return emptyTranslations.length === 0;
    }

    /**
     * Ensure that this range does not overlap with any of the existing summaries
     */
    private validateRange(): boolean {
        var currentMin = this.formRef.props.form.getFieldValue("scoreMin");
        var currentMax = this.formRef.props.form.getFieldValue("scoreMax");
        var errorString;

        var sum: UpdateActivitySummaryDTO;
        for (sum of this.state.allSummaries) {
            // Don't compare with same summary (editing existing summary)
            if (sum.activitySummaryId === this.state.currentActivitySummary.activitySummaryId) {
                continue;
            }

            // Overlaps with an existing summary range
            if (this.isOverlapping(currentMin, currentMax, sum.scoreMin, sum.scoreMax)) {
                errorString = (this.props.translate("admin.addEditSummary.rangeOverlapError") as string)
                    .replace("$min$", currentMin)
                    .replace("$max$", currentMax)
                    .replace("%min%", sum.scoreMin + "")
                    .replace("%max%", sum.scoreMax + "");

                this.error = errorString;
                return false;
            }
        }

        this.error = "";
        return true;
    }

    private handleInputChange(changedFields: any[]): void {
        let newState = {};
        this.validateRange();

        for (var key in changedFields) {
            if (changedFields.hasOwnProperty(key)) {
                let field: any = changedFields[key];

                // If key is the nested property
                if (key === 'translations') {
                    let fieldKey = '';
                    for (var fieldName in field) {
                        if (field.hasOwnProperty(fieldName)) {
                            fieldKey = fieldName;
                            break;
                        }
                    }
                    newState[key] = [...this.state.currentActivitySummary[key]];
                    newState[key][fieldKey].text = field[fieldKey].text.value;
                } else if (key === 'selectedCultureId') {
                    this.setState({ selectedCultureId: field.value });
                    return;
                }
                else {
                    newState[key] = field.value;
                }
            }
        }
        this.setState({
            currentActivitySummary: { ...this.state.currentActivitySummary, ...newState }
        });
    }

    private addActivitySummary(): void {
        let addActivitySummaryDTO: AddActivitySummaryDTO = {
            translations: this.state.currentActivitySummary.translations,
            activityId: this.state.currentActivitySummary.activityId,
            scoreMin: this.state.currentActivitySummary.scoreMin,
            scoreMax: this.state.currentActivitySummary.scoreMax,
        };

        ActivityApiController.addActivitySummary(addActivitySummaryDTO)
            .then(result => {
                message.success(this.props.translate('admin.addEditSummary.messageAdd'));
                this.setState({ isSubmitting: false });
                this.props.history.push(Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY_SUMMARY, this.state.currentActivitySummary.activityId, result.data));
                this.handleRedirect();
            }).catch(e => {
                message.error(this.props.translate('admin.addEditSummary.messageAddError'));
                this.setState({ isSubmitting: false });
            });
    }

    private updateActivitySummary(): void {
        ActivityApiController.updateActivitySummary(this.state.currentActivitySummary)
            .then(result => {
                message.success(this.props.translate('admin.addEditSummary.messageUpdate'));
                this.setState({ isSubmitting: false });
                this.props.history.push(Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY_SUMMARY, this.state.currentActivitySummary.activityId, this.state.currentActivitySummary.activitySummaryId));
                this.handleRedirect();
            }).catch(e => {
                message.error(this.props.translate('admin.addEditSummary.messageUpdateError'));
                this.setState({ isSubmitting: false });
            });
    }

    private handleSubmit(e): void {
        e.preventDefault();

        // Check again to make sure range is valid and don't allow submission if it isn't
        // Need to do this because it's separate from the actual form's validation
        if (!this.validateRange()) {
            return;
        }

        this.formRef.props.form.validateFields((error: any, values: any) => {
            if (!error) {
                this.setState({ isSubmitting: true });
                this.state.currentActivitySummary.activitySummaryId ? this.updateActivitySummary() : this.addActivitySummary();
            }
        });
    }

    /**
    * https://stackoverflow.com/questions/3269434/whats-the-most-efficient-way-to-test-two-integer-ranges-for-overlap
    */
    private isOverlapping(x1, x2, y1, y2): boolean {
        return Math.max(x1, y1) <= Math.min(x2, y2);
    }

    private handleRedirect(): void {
        let redirectUrl = Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY, this.state.currentActivitySummary.activityId, true);
        this.props.history.push(redirectUrl);
    }

    private handleCancel(e): void {
        this.props.history.push(Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY, this.state.currentActivitySummary.activityId));
    }

    private renderSaveButton(): JSX.Element {
        // Allow them to click save if translations are complete and there are no errors
        if (this.translationsAreCompleted() && !this.error) {
            return (
                <Button type="primary" onClick={this.handleSubmit} loading={this.state.isSubmitting}>
                    {this.props.translate("admin.common.saveButton")}
                </Button>
            );
        }

        return (
            <Tooltip placement="bottom" title={this.props.translate('admin.addEditSummary.saveTooltip')} arrowPointAtCenter>
                <Button type="primary" disabled>
                    {this.props.translate("admin.common.saveButton")}
                </Button>
            </Tooltip>
        );
    }

    render() {
        if (this.state.loading) {
            return <Spin />;
        }

        let formProps = {
            currentActivitySummary: this.state.currentActivitySummary,
            allCultures: this.state.allCultures,
            selectedCultureId: this.state.selectedCultureId,
            translate: this.props.translate,
            onChange: this.handleInputChange,
            activityMaxScore: this.state.activityMaxScore,
            allSummaries: this.state.allSummaries,
        } as ActivitySummaryFormProps;

        return (
            <div>
                <h2>
                    {!this.state.isUpdate && <Translate id="admin.addEditSummary.addHeader" />}
                    {this.state.isUpdate && <Translate id="admin.addEditSummary.editHeader" />}
                </h2>

                <ActivitySummaryForm wrappedComponentRef={ref => this.formRef = ref} {...formProps} />

                {/* Show score range error at the bottom of the form (under min/max score fields) */}
                <div>
                    <p style={{ color: "red" }}>
                        {this.error}
                    </p>
                </div>

                <div>
                    {this.renderSaveButton()}
                    <Divider type="vertical" />
                    <Button onClick={this.handleCancel}>
                        {this.props.translate("common.back")}
                    </Button>
                </div>

                <br /><br />


            </div>
        );
    }
}

export default withLocalize(AddEditActivitySummary);



// WEBPACK FOOTER //
// ./src/pages/admin/AddEditActivitySummary.tsx